import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Container, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { FacebookShareButton, TwitterShareButton } from 'react-share';
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';

import Image from '@plone/volto/components/theme/Image/Image';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { formatUrl } from '@plone/volto/helpers/Api/Api';
import { getBlocks, hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers/Url/Url';

import { FacebookLogo } from '../Icons/FacebookLogo';
import { TwitterLogo } from '../Icons/TwitterLogo';
import { homepageImageSources } from '../../helpers/homepageUtils';
import {
  getImageFromContent,
  getNewsImageStyle,
  getShareUrl,
} from '../../helpers/newsUtils';
import { resolveRichTextHtml } from '../../helpers/richTextHtml';
import { formatPublicationDate } from '../../helpers/formatPublicationDate';

const messages = defineMessages({
  otherNewsHeading: {
    id: 'Other news heading',
    defaultMessage: 'ALTRES NOTÍCIES',
  },
  previousSlide: {
    id: 'Previous slide',
    defaultMessage: 'Diapositiva anterior',
  },
  nextSlide: {
    id: 'Next slide',
    defaultMessage: 'Diapositiva següent',
  },
});

const NewsItemView = (props) => {
  const { content, location } = props;
  const intl = useIntl();
  const path = getBaseUrl(location?.pathname || '');
  const [otherNews, setOtherNews] = useState([]);
  const [visibleSlides, setVisibleSlides] = useState(4);

  const lead = homepageImageSources(content?.image, {
    minWidth: 800,
    fallback: 'large',
  });
  const inlineImage = lead.src ? '' : getImageFromContent(content?.text?.data);
  const html = resolveRichTextHtml(content?.text?.data);
  const shareUrl = getShareUrl(content['@id']);

  const itemLink = useCallback(
    (itemId) => {
      if (typeof window === 'undefined' || !itemId) {
        return itemId;
      }
      return window.location.pathname.replace(content.id, itemId);
    },
    [content.id],
  );

  const updateVisibleSlides = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const container = document.querySelector('.view-wrapper.csj-news-item');
    if (!container) {
      return;
    }
    const nextVisibleSlides = Math.max(
      1,
      Math.trunc(container.offsetWidth / 200),
    );
    setVisibleSlides((current) =>
      current === nextVisibleSlides ? current : nextVisibleSlides,
    );
  }, []);

  useEffect(() => {
    const parentId =
      content?.parent?.['@id'] ||
      (content?.['@id'] ? content['@id'].replace(/\/[^/]+$/, '') : null);
    if (!parentId) {
      return undefined;
    }

    const controller = new AbortController();
    const url = formatUrl(`${flattenToAppURL(parentId)}?fullobjects`);

    fetch(url, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Other news fetch failed: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setOtherNews(data.items || []);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [content]);

  useEffect(() => {
    updateVisibleSlides();
    if (typeof window === 'undefined') {
      return undefined;
    }
    window.addEventListener('resize', updateVisibleSlides);
    return () => window.removeEventListener('resize', updateVisibleSlides);
  }, [updateVisibleSlides]);

  if (hasBlocksData(content) && getBlocks(content).length > 0) {
    return <RenderBlocks {...props} path={path} />;
  }

  const carouselItems = otherNews.filter(
    (item) => item.review_state === 'published' && item.id !== content.id,
  );

  return (
    <Container className="view-wrapper csj-news-item">
      {lead.src ? (
        <Image
          className="ui image documentImage"
          alt={content.title || ''}
          title={content.title}
          src={lead.src}
          srcSet={lead.srcSet}
          sizes="100vw"
          width={lead.width}
          height={lead.height}
        />
      ) : null}
      {!lead.src && inlineImage ? (
        <Image
          className="ui image documentImage"
          alt={content.title || ''}
          title={content.title}
          src={inlineImage}
        />
      ) : null}
      {content.effective && (
        <div className="documentCreated">
          {formatPublicationDate(content.effective)}
        </div>
      )}
      {content.title && (
        <h1 className="documentFirstHeading csj-heading1">
          {content.title}
          {content.subtitle && ` - ${content.subtitle}`}
        </h1>
      )}
      {content.description && (
        <p className="documentDescription">{content.description}</p>
      )}
      {html && (
        <div
          className="documentText"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}

      {typeof window !== 'undefined' && (
        <div className="news-item-social-container">
          <TwitterShareButton
            url={shareUrl}
            title={content.title}
            description={content.description}
          >
            <Button circular icon className="news-social-icon twitter-icon">
              <TwitterLogo width={20} height={20} />
            </Button>
          </TwitterShareButton>
          <FacebookShareButton url={shareUrl} quote={content.title}>
            <Button circular icon className="news-social-icon">
              <FacebookLogo width={20} height={20} />
            </Button>
          </FacebookShareButton>
        </div>
      )}

      {carouselItems.length > 0 && (
        <div className="rollover-other-container-news">
          <p className="other-container-news-heading">
            {intl.formatMessage(messages.otherNewsHeading)}
          </p>
          <CarouselProvider
            naturalSlideWidth={200}
            naturalSlideHeight={200}
            totalSlides={carouselItems.length}
            visibleSlides={visibleSlides}
          >
            <Slider>
              {carouselItems.map((item, index) => (
                <Slide key={item['@id']} index={index}>
                  <Link to={itemLink(item.id)}>
                    <div className="news-item">
                      <div
                        className="news-item-image"
                        style={getNewsImageStyle(item)}
                      />
                      <div className="news-item-date">
                        {formatPublicationDate(item.effective)}
                      </div>
                      <h4 className="news-item-title csj-heading4">
                        {item.title}
                      </h4>
                    </div>
                  </Link>
                </Slide>
              ))}
            </Slider>

            <div className="rollover-actions-container">
              <ButtonBack
                className="ui icon button"
                aria-label={intl.formatMessage(messages.previousSlide)}
              >
                <Icon name="chevron left" />
              </ButtonBack>
              <ButtonNext
                className="ui icon button"
                aria-label={intl.formatMessage(messages.nextSlide)}
              >
                <Icon name="chevron right" />
              </ButtonNext>
            </div>
          </CarouselProvider>
        </div>
      )}
    </Container>
  );
};

NewsItemView.propTypes = {
  content: PropTypes.objectOf(PropTypes.any).isRequired,
  location: PropTypes.objectOf(PropTypes.any),
};

export default NewsItemView;
